import React, { useState, useEffect } from "react";
import axiosInstance from "../utils/axiosInstance";

import { useNavigate, useParams } from "react-router-dom";
import backendURL, { google_login_api, linkedin_client_id, linkedin_redirect_uri, SETUP_COMPLETE_LOCAL_KEY } from "../config";

export default function VerifiedEmail() {
    // --- States for initial verification ---
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null); // Error from initial token verification
    const [success, setSuccess] = useState(false);
    const [message, setMessage] = useState(""); // Success message from initial verification

    // --- States for resend action ---
    const [resendLoading, setResendLoading] = useState(false);
    const [resendError, setResendError] = useState(null);
    const [resendSuccessMessage, setResendSuccessMessage] = useState(null);
    const [emailForResend, setEmailForResend] = useState(""); // Email input for resend

    const navigate = useNavigate();
    const { token } = useParams();

    useEffect(() => {
        document.title = "Email Verification | KlonIT Web Application";

        const verifyEmail = async () => {
            setLoading(true);
            setError(null); // Clear previous errors
            try {
                const response = await axiosInstance.get(`/verify/${token}`);
                // console.log("API Response:", response); // Debug: Log API response

                // Safely parse data, handle potential stringified JSON
                const responseData = typeof response.data.data === 'string' ? JSON.parse(response.data.data) : response.data.data;
                
                const {customer_portal_token ,  user, message: apiMessage } = responseData;
                localStorage.setItem("user", JSON.stringify(user));
                localStorage.setItem('customer_portal_token', customer_portal_token);
                console.log("Verifying...")
                if (!user.profile || user.active_team.has_pending_terms || !user.active_team.subscription) {
                    localStorage.removeItem(SETUP_COMPLETE_LOCAL_KEY);
                    window.location.href = "/SignUp-Process-Steps";
                }else{
                    window.location.href = "/chatbots";
                }

                setSuccess(true);
                setMessage(apiMessage || "Email successfully verified!"); // Use API message or default

            } catch (err) {
                console.error("API Error:", err); // Debug: Log the full error
                const errorMessage = err.response?.data?.message || "Invalid or expired verification link.";
                setError(errorMessage);
                setSuccess(false); // Ensure success is false on error
            } finally {
                setLoading(false);
            }
        };

        if (token) {
          verifyEmail();
        } else {
            setError("Verification token is missing from the URL.");
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]); // Only re-run if the token changes

    const handleProceedNext = () => {
        // Navigate to the next step after successful verification
        navigate("/SignUp-Process-Steps"); // Or wherever verified users should go
    };

    const handleResendEmail = async (e) => {
        e.preventDefault(); // Prevent default form submission if wrapped in form
        if (!emailForResend) {
            setResendError("Please enter your email address.");
            return;
        }
        // Basic email format check (optional but recommended)
        if (!/\S+@\S+\.\S+/.test(emailForResend)) {
             setResendError("Please enter a valid email address.");
             return;
        }

        setResendLoading(true);
        setResendError(null);
        setResendSuccessMessage(null);

        try {
            const response = await axiosInstance.post(`/resend-verification`, {
                email: emailForResend,
            });
            setResendSuccessMessage(response.data.message);
            // Optionally clear the email field after success
            // setEmailForResend("");
        } catch (err) {
            console.error("Resend API Error:", err);
            const errorMessage = err.response?.data?.message || "Failed to resend email. Please try again.";
            setResendError(errorMessage);
        } finally {
            setResendLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            <main className="d-flex w-100">
                <div className="container d-flex flex-column">
                    <div className="row vh-100">
                        <div className="col-sm-10 col-md-8 col-lg-6 mx-auto d-table h-100">
                            <div className="d-table-cell align-middle">
                                <div className="text-center mt-4">
                                    <h1 className="h2">
                                        {success ? "Email Verified!" : "Email Verification"}
                                    </h1>
                                    <p className="lead">
                                        {/* Display initial verification status */}
                                        {success ? message : error ? error : "Checking verification status..."}
                                    </p>
                                </div>

                                {success && (
                                    <div className="card shadow-sm">
                                        <div className="card-body">
                                            <div className="alert alert-success">
                                                {message || "Your email has been successfully verified."}
                                            </div>
                                            <div className="d-grid gap-2 mt-3">
                                                <button
                                                    className="btn btn-lg btn-primary"
                                                    onClick={handleProceedNext}
                                                >
                                                    Proceed to Dashboard
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {error && !success && ( // Only show resend option if initial verification failed
                                    <div className="card shadow-sm">
                                        <div className="card-body">
                                            <div className="m-sm-3">
                                                {/* Display initial error */}
                                                <div className="alert alert-danger mb-3">
                                                    {error}
                                                </div>

                                                {/* Display Resend Action Feedback */}
                                                {resendError && (
                                                    <div className="alert alert-warning mt-3">{resendError}</div>
                                                )}
                                                {resendSuccessMessage && (
                                                    <div className="alert alert-info mt-3">{resendSuccessMessage}</div>
                                                )}

                                                <form onSubmit={handleResendEmail}>
                                                     <div className="mb-3">
                                                        <label className="form-label">Your Email Address</label>
                                                        <input
                                                            className={`form-control form-control-lg ${resendError && !emailForResend ? 'is-invalid' : ''}`}
                                                            type="email"
                                                            name="email"
                                                            placeholder="Enter your email to resend verification"
                                                            value={emailForResend}
                                                            onChange={(e) => setEmailForResend(e.target.value)}
                                                            required
                                                            disabled={resendLoading}
                                                        />
                                                        {resendError && !emailForResend && (
                                                            <div className="invalid-feedback">
                                                                Email is required.
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="d-grid gap-2 mt-3">
                                                        <button
                                                            type="submit" // Use type="submit" for form
                                                            className="btn btn-lg btn-secondary"
                                                            disabled={resendLoading}
                                                        >
                                                            {resendLoading ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                                    Sending...
                                                                </>
                                                            ) : (
                                                                "Resend Verification Email"
                                                            )}
                                                        </button>
                                                    </div>
                                                 </form>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}